import AnalysisDataCriteria, { BasicAnalysisDataCriteria, CreateAnalysisDataCriteria } from 'use-case/analysis/analysisCriteria';
import { AnalysisPreset } from 'domain/entity/analysis';

const isAnalysisDataCriteria = (criteria: BasicAnalysisDataCriteria): criteria is AnalysisDataCriteria =>
    (criteria as AnalysisDataCriteria).analysisId !== undefined;

const isCreateDataCriteria = (criteria: BasicAnalysisDataCriteria): criteria is CreateAnalysisDataCriteria =>
    (criteria as CreateAnalysisDataCriteria).parentId !== undefined && (criteria as CreateAnalysisDataCriteria).parentType !== undefined;

const serializePreset = (preset: AnalysisPreset) => {
    const { visualisationOverride, ...presetRest } = preset;
    if (!visualisationOverride) return presetRest;
    return {
        ...presetRest,
        visualisation_override: {
            combined_subtype: visualisationOverride.combinedSubtype,
            fill_area: visualisationOverride.fillArea,
            stack: visualisationOverride.stack,
        },
    };
};

export const parseAnalysisDataCriteria = (clientCriteria: BasicAnalysisDataCriteria) => {
    const { visualisation, compareTimeshift, atomsVisualisation, compareAlignment, title, ...rest } = clientCriteria;

    let criteriaWithoutId;

    if (isCreateDataCriteria(clientCriteria)) {
        const restWithoutParentInfo = { ...rest } as Partial<CreateAnalysisDataCriteria>;
        delete restWithoutParentInfo.parentId;
        delete restWithoutParentInfo.parentType;
        criteriaWithoutId = restWithoutParentInfo;
    } else if (isAnalysisDataCriteria(clientCriteria)) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { analysisId, saveChanges, ...restWithoutId } = rest as AnalysisDataCriteria;
        criteriaWithoutId = restWithoutId;
    } else {
        criteriaWithoutId = rest;
    }

    return {
        ...criteriaWithoutId,
        presets: (criteriaWithoutId.presets ?? []).map(serializePreset),
        compare_timeshift: compareTimeshift,
        visualisation: {
            type: visualisation.type,
            combined_subtype: visualisation.combinedSubtype,
            indicator_subtype: visualisation.indicatorSubtype,
            fill_area: visualisation.fillArea,
            reducer_operation: visualisation.reducerOperation,
            invert_axes: visualisation.invertAxes,
            stack: visualisation.stack,
            accumulator_operation: visualisation.accumulatorOperation,
            magnitude_preferences: (visualisation.magnitudePreferences ?? []).length > 0 ? visualisation.magnitudePreferences : null,
            user_prompt: visualisation.userPrompt,
            enable_ai_description: visualisation.enableAiDescription,
        },
        atoms_meta_data: [],
        atoms_visualisation: atomsVisualisation,
        compare_alignment: compareAlignment,
        title,
    };
};

type JsonPresetVisualisationOverride = {
    ['combined_subtype']: string;
    ['fill_area']: boolean;
    stack: boolean;
};

type JsonPreset = {
    key: string;
    fields: { key: string; value: string }[];
    ['visualisation_override']?: JsonPresetVisualisationOverride;
};

type JsonAnalysis = {
    ['compare_timeshift']: number;
    ['atoms_visualisation']: JsonAtomVisualisation[];
    comparisons: JsonAtomComparison[];
    presets: JsonPreset[];
};

type JsonAtomVisualisation = {
    chart: {
        ['line_border']: string;
    };
};

type JsonAtomComparison = {
    ['compare_id']: string;
    ['against_id']: string;
    ['lower_is']: string;
    ['margin_percentage']: number;
};

const parsePreset = ({ visualisation_override: visualisationOverrideRaw, ...rest }: JsonPreset) => {
    if (!visualisationOverrideRaw) return rest;
    return {
        ...rest,
        visualisationOverride: {
            combinedSubtype: visualisationOverrideRaw.combined_subtype,
            fillArea: visualisationOverrideRaw.fill_area,
            stack: visualisationOverrideRaw.stack,
        },
    };
};

export const parseJsonAnalysis = (json: JsonAnalysis) => {
    const parseAtomVisualisation = (jsonToParse: JsonAtomVisualisation) => ({
        ...jsonToParse,
        chart: {
            ...jsonToParse.chart,
            lineBorder: jsonToParse.chart.line_border,
        },
    });
    const parseAtomComparison = (jsonToParse: JsonAtomComparison) => ({
        ...jsonToParse,
        compareId: jsonToParse.compare_id,
        againstId: jsonToParse.against_id,
        lowerIs: jsonToParse.lower_is,
        marginPercentage: jsonToParse.margin_percentage,
    });

    return {
        ...json,
        presets: json.presets?.map(parsePreset),
        compareTimeshift: json.compare_timeshift,
        atomsVisualisation: json.atoms_visualisation ? json.atoms_visualisation.map(parseAtomVisualisation) : [],
        comparisons: json.comparisons ? json.comparisons.map(parseAtomComparison) : [],
    };
};
